import React from "react";
import { Badge, Card, CardBody, Col, Row } from "reactstrap";
import { DateOnly, DateTime } from "components/common/Date";
import { useEmployeeOverview } from "./EmployeeOverviewContext";
import {
  departmentDisplayName,
  designationDisplayName,
  employmentCategoryDisplayName,
  type Employee,
} from "../types/employee.types";

function EmptyValue() {
  return <span className="text-muted fst-italic fw-normal">—</span>;
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-4">
      <p className="text-muted small mb-1">{label}</p>
      <div className="text-body fw-medium">{children}</div>
      {hint ? <p className="text-muted small mt-1 mb-0">{hint}</p> : null}
    </div>
  );
}

function formatWorkAddress(emp: Employee | null): React.ReactNode {
  if (!emp) return <EmptyValue />;
  const line1 = emp.address?.trim();
  const cityState = [emp.city?.trim(), emp.state?.trim()]
    .filter(Boolean)
    .join(", ");
  const pin = emp.pincode?.trim();
  const line2 = [cityState, pin]
    .filter(Boolean)
    .join(cityState && pin ? " " : "");

  if (!line1 && !line2) return <EmptyValue />;

  return (
    <div className="text-body">
      {line1 ? <div>{line1}</div> : null}
      {line2 ? <div>{line2}</div> : null}
    </div>
  );
}

const OverviewTab = () => {
  const { employee } = useEmployeeOverview();
  const user = employee?.user;

  const empName =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() ||
    [employee?.firstName, employee?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    "";
  const workEmail = (user?.email ?? employee?.email ?? "").trim();
  const workPhone = (user?.phone ?? employee?.phone ?? "").trim();

  const dept = employee ? departmentDisplayName(employee) : "—";
  const desig = employee ? designationDisplayName(employee) : "—";
  const empCat = employee ? employmentCategoryDisplayName(employee) : "—";

  return (
    <React.Fragment>
      <Row className="g-3 mb-4">
        <Col lg={6} className="d-flex flex-column gap-3">
          <Card className="border shadow-none mb-0">
            <CardBody className="p-4">
              <div className="d-flex align-items-start gap-2 mb-3">
                <span className="avatar-xs d-inline-flex align-items-center justify-content-center rounded bg-primary-subtle text-primary">
                  <i className="ri-briefcase-line fs-18" />
                </span>
                <div>
                  <h6 className="mb-0 text-body">Work</h6>
                  <p className="text-muted small mb-0">
                    Role, start date, and employee ID on file.
                  </p>
                </div>
              </div>
              <Field label="Employee ID">
                {employee?.code?.trim() ? (
                  <span className="font-monospace">{employee.code}</span>
                ) : (
                  <EmptyValue />
                )}
              </Field>
              <Field label="Full name">{empName || <EmptyValue />}</Field>
              <Field label="Department">
                {dept !== "—" ? dept : <EmptyValue />}
              </Field>
              <Field
                label="Job title"
                hint="Also called designation in HR records."
              >
                {desig !== "—" ? desig : <EmptyValue />}
              </Field>
              <Field
                label="Employment category"
                hint="e.g. probation, permanent, contract, intern."
              >
                {empCat !== "—" ? empCat : <EmptyValue />}
              </Field>
              <Field label="Join date" hint="First day with the company.">
                {employee?.joinDate ? (
                  <DateOnly date={employee.joinDate} />
                ) : (
                  <EmptyValue />
                )}
              </Field>
            </CardBody>
          </Card>

          <Card className="border shadow-none mb-0">
            <CardBody className="p-4">
              <div className="d-flex align-items-start gap-2 mb-3">
                <span className="avatar-xs d-inline-flex align-items-center justify-content-center rounded bg-info-subtle text-info">
                  <i className="ri-map-pin-line fs-18" />
                </span>
                <div>
                  <h6 className="mb-0 text-body">Location</h6>
                  <p className="text-muted small mb-0">
                    Work address and postal details.
                  </p>
                </div>
              </div>
              <Field label="Address">
                {formatWorkAddress(employee ?? null)}
              </Field>
              <Field
                label="Blood group"
                hint="Useful for emergencies or medical benefits."
              >
                {employee?.bloodGroup?.trim() ? (
                  <Badge color="light" className="text-body border fw-medium">
                    {employee.bloodGroup}
                  </Badge>
                ) : (
                  <EmptyValue />
                )}
              </Field>
            </CardBody>
          </Card>
        </Col>

        <Col lg={6} className="d-flex flex-column gap-3">
          <Card className="border shadow-none mb-0">
            <CardBody className="p-4">
              <div className="d-flex align-items-start gap-2 mb-3">
                <span className="avatar-xs d-inline-flex align-items-center justify-content-center rounded bg-success-subtle text-success">
                  <i className="ri-contacts-line fs-18" />
                </span>
                <div>
                  <h6 className="mb-0 text-body">Contact</h6>
                  <p className="text-muted small mb-0">
                    Work email and phone from the HR profile.
                  </p>
                </div>
              </div>
              <Field label="Work email">
                {workEmail ? (
                  <a
                    href={`mailto:${workEmail}`}
                    className="link-primary text-break"
                  >
                    {workEmail}
                  </a>
                ) : (
                  <EmptyValue />
                )}
              </Field>
              <Field label="Work phone">
                {workPhone ? (
                  <a
                    href={`tel:${workPhone}`}
                    className="text-body text-decoration-none"
                  >
                    {workPhone}
                  </a>
                ) : (
                  <EmptyValue />
                )}
              </Field>
            </CardBody>
          </Card>

          {user ? (
            <Card className="border shadow-none mb-0">
              <CardBody className="p-4">
                <div className="d-flex align-items-start gap-2 mb-3">
                  <span className="avatar-xs d-inline-flex align-items-center justify-content-center rounded bg-secondary-subtle text-secondary">
                    <i className="ri-user-settings-line fs-18" />
                  </span>
                  <div>
                    <h6 className="mb-0 text-body">Linked login account</h6>
                    <p className="text-muted small mb-0">
                      How this employee appears in the app for sign-in.
                    </p>
                  </div>
                </div>
                <Field label="Name on account">
                  {[user.firstName, user.lastName].filter(Boolean).join(" ") ||
                    "—"}
                </Field>
                <Field label="Email">
                  {user.email ? (
                    <a
                      href={`mailto:${user.email}`}
                      className="link-primary text-break"
                    >
                      {user.email}
                    </a>
                  ) : (
                    <EmptyValue />
                  )}
                </Field>
                <Field label="Phone">
                  {user.phone?.trim() ? (
                    <a
                      href={`tel:${user.phone}`}
                      className="text-body text-decoration-none"
                    >
                      {user.phone}
                    </a>
                  ) : (
                    <EmptyValue />
                  )}
                </Field>
                <Field label="Roles">
                  {user.roles?.length ? (
                    <div className="d-flex flex-wrap gap-1">
                      {user.roles.map((r) => (
                        <Badge
                          key={r.id}
                          color="primary"
                          className="fw-medium bg-primary-subtle text-primary"
                        >
                          {r.name}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <EmptyValue />
                  )}
                </Field>
                <Field label="Last seen">
                  {user.lastSeenAt ? (
                    <DateTime date={user.lastSeenAt} />
                  ) : (
                    <EmptyValue />
                  )}
                </Field>
              </CardBody>
            </Card>
          ) : null}
        </Col>
      </Row>

      {(employee?.createdAt ||
        employee?.updatedAt ||
        (employee?.deletedAt != null && employee.deletedAt !== "")) && (
        <Row className="mt-3">
          <Col>
            <Card className="border shadow-none mb-0">
              <CardBody className="p-4">
                <h6 className="mb-1 text-body">Record history</h6>
                <p className="text-muted small mb-3">
                  When this profile was created and last changed in the system.
                </p>
                <Row className="gy-3">
                  {employee?.createdAt ? (
                    <Col sm={6} md={4}>
                      <p className="text-muted small mb-1">Profile created</p>
                      <p className="mb-0 fw-medium fs-14">
                        <DateTime date={employee.createdAt} />
                      </p>
                    </Col>
                  ) : null}
                  {employee?.updatedAt ? (
                    <Col sm={6} md={4}>
                      <p className="text-muted small mb-1">Last updated</p>
                      <p className="mb-0 fw-medium fs-14">
                        <DateTime date={employee.updatedAt} />
                      </p>
                    </Col>
                  ) : null}
                  {employee?.deletedAt != null && employee.deletedAt !== "" ? (
                    <Col sm={6} md={4}>
                      <p className="text-muted small mb-1">Removed</p>
                      <p className="mb-0 fw-medium text-danger fs-14">
                        <DateTime date={employee.deletedAt} />
                      </p>
                    </Col>
                  ) : null}
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </React.Fragment>
  );
};

export default OverviewTab;
